import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ActionButton } from '@rivethealth/data/buttons';
import { HighlightIndices, Node, Option } from '@rivethealth/data/options';
import Fuse, { FuseResult } from 'fuse.js';
import { findIndex } from 'lodash';
import { ObservableInput } from 'observable-input';
import {
  BehaviorSubject,
  combineLatest,
  merge,
  Observable,
  of,
  Subject,
} from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  scan,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Loadable, success } from '../../common/helpers/loadable';
import { ChangeService } from '../core/change.service';
import { replayLast } from '../rx/util';
import { OptionSelectNodeDirective } from './option-select-node.directive';
import { RecentOptionsService } from './recent-dropdown-options.service';

export interface PinSetting {
  pinned: boolean;
}

export type OptionsLoader = (
  count: number,
  filter: string | null,
  ids: string[] | null,
) => Observable<Loadable<{ options: Option[]; count: number }>>;

const OPTIONS_LIMIT = 100;

/**
 * An option select with optional text search
 *
 * Provide one of options or optionsLoader as input
 *
 * Provide multiSelect=true and selectedOptionIds, or
 * multiSelect=false and selectedOptionId
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'option-select',
  styleUrls: ['./option-select.component.scss'],
  templateUrl: './option-select.component.html',
  providers: [ChangeService],
})
export class OptionSelectComponent<T = unknown> {
  constructor(private readonly recentOptionsService: RecentOptionsService) {}

  @Input()
  dragScrollable: boolean = false;

  @ObservableInput()
  @Input('disabled')
  readonly _disabled!: Observable<boolean>;
  readonly defaultedDisabled = this._disabled.pipe(startWith(false));

  @ObservableInput()
  @Input('filterPositionBottom')
  readonly _filterPositionBottom!: Observable<boolean>;
  readonly defaultedFilterPositionBottom = this._filterPositionBottom.pipe(
    startWith(false),
  );

  @Input()
  readonly all: boolean = false;

  @ObservableInput()
  @Input('exact')
  readonly _exact!: Observable<boolean>;
  readonly defaultedExact = this._exact.pipe(startWith(false));

  @ObservableInput()
  @Input('filterable')
  readonly _filterable!: Observable<boolean>;
  readonly defaultedFilterable: Observable<boolean> = this._filterable.pipe(
    startWith(false),
  );

  @Input()
  pinned!: PinSetting[];

  @ObservableInput()
  @Input('minSelectedCount')
  readonly _minSelectedCount!: Observable<number>;
  readonly defaultedMinSelectedCount: Observable<number> =
    this._minSelectedCount.pipe(startWith(0));

  @Input()
  maxSelectedCount: number | null = null;

  readonly filter = new BehaviorSubject<string>('');

  @Output()
  readonly filterChange = new EventEmitter<string>();

  @Output()
  readonly tabChange = new EventEmitter<number>();

  @ObservableInput()
  @Input('currentTab')
  readonly _tab!: Observable<string>;
  readonly currentTab: Observable<string> = this._tab.pipe(startWith(''));

  @Input() tabNames: string[] | undefined;

  @ObservableInput()
  @Input('options')
  readonly _options!: Observable<Option[]>;
  readonly defaultedOptions: Observable<Option[]> = this._options.pipe(
    startWith([]),
    map(options => options || []),
  );

  @Input('optionsLoader')
  set optionsLoader(loader: OptionsLoader) {
    if (loader) {
      this.defaultedOptionsLoader.next(loader);
      this.usingLoader = true;
    }
  }
  readonly defaultedOptionsLoader: BehaviorSubject<OptionsLoader | null> =
    new BehaviorSubject<OptionsLoader | null>(null);

  readonly debouncedFilter = this.defaultedOptionsLoader.pipe(
    switchMap(loader =>
      loader ? this.filter.pipe(debounceTime(250)) : this.filter,
    ),
    startWith(''),
  );

  @Input()
  recentIdentifier: string | null;
  recentOptionsSubject = new BehaviorSubject<undefined>(undefined);

  @Input()
  readonly draggable: boolean = false;

  @Input()
  readonly showAvatars: boolean = false;

  @Input()
  readonly forceSortOnBestMatch: boolean = false;

  @Input('optionActionButton')
  optionActionButton: ActionButton;

  @Input()
  selectedEnabled: boolean = true;

  @Input()
  allowSelectingEmpty: boolean = true;

  @Input()
  displayId: boolean;

  @Input()
  allowMultiSelectedSingleSelect: boolean = false;

  showSelected: boolean = false;
  showAllNodes: boolean = false;

  usingLoader: boolean = false;

  readonly optionsLimitIncrement: BehaviorSubject<number> = new BehaviorSubject(
    OPTIONS_LIMIT,
  );

  readonly optionsLimit = this.optionsLimitIncrement.pipe(
    scan((previous, increment) => previous + increment),
  );

  readonly optionsLoadable = combineLatest([
    this.defaultedOptions,
    this.defaultedOptionsLoader,
    this.debouncedFilter,
    this.defaultedFilterable,
    this.optionsLimit,
  ]).pipe(
    switchMap(([options, loader, filter, filterable, optionsLimit]) => {
      if (loader) {
        return loader(optionsLimit, filterable ? filter : '', null);
      } else {
        return of({
          loading: false,
          erred: false,
          data: {
            options: options,
            count: options.length,
          },
        });
      }
    }),
    replayLast(),
  );

  @Output()
  readonly options = this.optionsLoadable.pipe(success(), replayLast());

  @Output()
  onDragEvent = new EventEmitter<[number, number]>();

  @Output()
  onPinnedEvent = new EventEmitter<[string, boolean]>();

  @ObservableInput()
  @Input('multiSelect')
  readonly _multiSelect!: Observable<boolean>;
  readonly defaultedMultiSelect: Observable<boolean> = this._multiSelect.pipe(
    startWith(false),
  );

  @ObservableInput()
  @Input('selectedOptionId')
  readonly _selectedOptionId!: Observable<string>;
  readonly defaultedSelectedOptionId = combineLatest([
    this._selectedOptionId,
    this.defaultedMultiSelect,
  ]).pipe(
    filter(([_, multiSelect]) => !multiSelect),
    map(([id, _]) => id),
    startWith(null),
  );

  @ObservableInput()
  @Input('selectedOptionIds')
  readonly _selectedOptionIds!: Observable<string[]>;
  readonly defaultedSelectedOptionIds = combineLatest([
    this._selectedOptionIds,
    this.defaultedMultiSelect,
  ]).pipe(
    filter(
      ([_, multiSelect]) =>
        this.allowMultiSelectedSingleSelect || !!multiSelect,
    ),
    map(([ids, _]) => ids),
    startWith(null),
  );

  readonly combinedSelectedIds = combineLatest([
    this.defaultedSelectedOptionId,
    this.defaultedSelectedOptionIds,
    this.defaultedMultiSelect,
  ]).pipe(
    map(([id, ids, multi]) =>
      multi || this.allowMultiSelectedSingleSelect
        ? ids || []
        : id === null
          ? []
          : [id],
    ),
  );

  readonly atMinSelected = combineLatest([
    this.combinedSelectedIds,
    this.defaultedMinSelectedCount,
  ]).pipe(map(([{ length: count }, min]) => count <= min));

  readonly atMaxSelected = this.combinedSelectedIds.pipe(
    map(
      ({ length: count }) =>
        this.maxSelectedCount && count >= this.maxSelectedCount,
    ),
  );

  @Output() readonly optionIdSelected = new Subject<string>();

  @Output() readonly optionIdPinned = new Subject<string>();

  @Output() readonly optionIdUnpinned = new Subject<string>();

  @Output() readonly optionIdUnselected = new Subject<string>();

  @Output() readonly selectedOptionIdsChange = new Subject<string[]>();

  @Output() readonly selectedOptionIdChange = new Subject<string>();

  @ContentChild(OptionSelectNodeDirective, { static: false })
  readonly nodeDirective: OptionSelectNodeDirective<T>;

  @ViewChildren('nodeRef', { read: ElementRef })
  readonly nodeRef: QueryList<ElementRef>;

  public externalKeyPress = new Subject<string>();
  private internalKeyPress = new Subject<string>();

  minWidth = 0;

  private fuzzySearch = combineLatest([
    this.defaultedOptions,
    this.defaultedExact,
    this.defaultedFilterable,
  ]).pipe(
    map(([options, exact, filterable]) => {
      const hasHeaders = !!options.find(o => !!o.header);
      return filterable
        ? new Fuse(
            options.filter(o => !o.header),
            {
              keys: ['title', 'subtitle', 'subtext', 'searchOnlyText'],
              includeMatches: true,
              shouldSort: !hasHeaders || this.forceSortOnBestMatch,
              threshold: exact ? 0.0 : 0.6,
            },
          )
        : null;
    }),
  );

  private forcedSortNodeFromMatch(match: FuseResult<Option>): Node {
    // a node is a clone of the item (option),
    // plus the additional fields to make a Node
    let titleHighlightIndices: HighlightIndices = [];
    let subtitleHighlightIndices: HighlightIndices = [];
    let subtextHighlightIndices: HighlightIndices = [];
    for (const fuseMatch of match.matches || []) {
      switch (fuseMatch.key) {
        case 'title':
          titleHighlightIndices = fuseMatch.indices.concat();
          break;
        case 'subtitle':
          subtitleHighlightIndices = fuseMatch.indices.concat();
          break;
        case 'subtext':
          subtextHighlightIndices = fuseMatch.indices.concat();
      }
    }
    return Object.assign(
      {
        selected: false,
        highlighted: false,
        titleHighlightIndices,
        subtitleHighlightIndices,
        subtextHighlightIndices,
      },
      {
        ...match.item,
        subtext: match.item.forcedSortSubtext,
      },
    );
  }

  private nodeFromMatch(match: FuseResult<Option>): Node {
    // a node is a clone of the item (option),
    // plus the additional fields to make a Node
    let titleHighlightIndices: HighlightIndices = [];
    let subtitleHighlightIndices: HighlightIndices = [];
    let subtextHighlightIndices: HighlightIndices = [];
    for (const fuseMatch of match.matches || []) {
      switch (fuseMatch.key) {
        case 'title':
          titleHighlightIndices = fuseMatch.indices.concat();
          break;
        case 'subtitle':
          subtitleHighlightIndices = fuseMatch.indices.concat();
          break;
        case 'subtext':
          subtextHighlightIndices = fuseMatch.indices.concat();
      }
    }
    return Object.assign(
      {
        selected: false,
        highlighted: false,
        titleHighlightIndices,
        subtitleHighlightIndices,
        subtextHighlightIndices,
      },
      match.item,
    );
  }

  private nodeFromOption(option: Option): Node {
    return {
      ...option,
      selected: false,
      highlighted: false,
      titleHighlightIndices: [],
      subtitleHighlightIndices: [],
      subtextHighlightIndices: [],
    };
  }

  private addMatchHeaders(
    options: Option[],
    matches: FuseResult<Option>[],
  ): Node[] {
    const matchIds: { [key: string]: boolean } = {};
    for (const match of matches) {
      matchIds[match.item.id] = true;
    }

    const headersByMatchId: { [key: string]: Option } = {};
    let lastHeader: Option | null = null;
    for (const node of options) {
      if (node.header) {
        lastHeader = node;
      }
      if (matchIds[node.id] && !!lastHeader) {
        headersByMatchId[node.id] = lastHeader;
      }
    }

    const result: Node[] = [];
    const seen: { [key: string]: boolean } = {};
    for (const match of matches) {
      const header = headersByMatchId[match.item.id];

      if (this.forceSortOnBestMatch) {
        result.push(this.forcedSortNodeFromMatch(match));
      } else {
        if (!!header && !seen[header.title]) {
          result.push(this.nodeFromOption(header));
          seen[header.title] = true;
        }
        result.push(this.nodeFromMatch(match));
      }
    }
    return result;
  }

  private findMatchedNodes(
    options: Option[],
    filter: string,
    fuzzySearch: Fuse<Option>,
  ): Node[] {
    let matchedNodes: Node[] = [];
    if (filter.length > 0) {
      const matches = fuzzySearch.search(filter);
      if (matches.length) {
        matchedNodes = this.addMatchHeaders(options, matches);
      }
    } else {
      matchedNodes = options.map(option => this.nodeFromOption(option));
    }
    return matchedNodes;
  }

  matchedNodes: Observable<{ nodes: Node[]; count: number }> = combineLatest([
    this.filter,
    this.options,
    this.defaultedFilterable,
    this.fuzzySearch,
  ]).pipe(
    map(([filter, options, filterable, fuzzySearch]) => {
      if (!this.usingLoader && filterable && fuzzySearch) {
        const matched = this.findMatchedNodes(
          options.options,
          filter,
          fuzzySearch,
        );
        return {
          count: matched.length,
          nodes: matched,
        };
      }
      return {
        count: options.count,
        nodes: options.options.map(option => this.nodeFromOption(option)),
      };
    }),
    replayLast(),
  );

  private recentNodes(
    options: Map<string | number, Option>,
    ids: string[],
    addAllHeader: boolean,
  ): Node[] {
    const availableRecentOptions: Option[] = [];
    ids.forEach(id => {
      if (options.has(id)) {
        availableRecentOptions.push(options.get(id)!);
      }
    });
    const recentOptions = [
      { id: '', title: 'Recent', subtitle: null, header: true },
      ...availableRecentOptions,
    ];
    if (addAllHeader) {
      recentOptions.push({
        id: '',
        title: 'All',
        subtitle: null,
        header: true,
      });
    }
    return recentOptions.map(option => this.nodeFromOption(option));
  }

  nodesRecent: Observable<Node[]> = combineLatest([
    this.matchedNodes,
    this.options.pipe(
      map(options =>
        this.recentIdentifier
          ? new Map(options.options.map(option => [option.id, option]))
          : null,
      ),
    ),
    this.filter,
    this.recentOptionsSubject,
  ]).pipe(
    map(([data, options, filter]) => {
      const recentIds = this.recentOptionsService.getLocalLimited(
        this.recentIdentifier,
      );
      const [firstNode] = data.nodes;
      return firstNode && recentIds != null && options != null && filter === ''
        ? [
            ...this.recentNodes(options, recentIds, !firstNode.header || false),
            ...data.nodes,
          ]
        : data.nodes;
    }),
  );

  nodesLimited: Observable<Node[]> = this.nodesRecent.pipe(
    withLatestFrom(this.defaultedFilterable),
    map(([nodes, filterable]) =>
      filterable && !this.usingLoader
        ? (nodes || []).slice(0, OPTIONS_LIMIT)
        : nodes,
    ),
  );

  private getNextIndex(currentIndex: number, nodes: Node[]) {
    let newIndex = currentIndex;
    let found = false;
    while (!found) {
      if (newIndex >= nodes.length - 1) {
        return newIndex;
      }
      newIndex += 1;
      found = !nodes[newIndex].disabled && !nodes[newIndex].header;
    }
    return newIndex;
  }

  private getPreviousIndex(currentIndex: number, nodes: Node[]) {
    let newIndex = currentIndex;
    let found = false;
    while (!found) {
      if (newIndex <= 0) {
        return newIndex;
      }
      newIndex -= 1;
      found = !nodes[newIndex].disabled && !nodes[newIndex].header;
    }
    return newIndex;
  }

  private keyPress = merge(this.externalKeyPress, this.internalKeyPress);

  private firstSelectedId = combineLatest([
    this.defaultedSelectedOptionId,
    this.defaultedSelectedOptionIds,
    this.defaultedMultiSelect,
    this.nodesRecent,
  ]).pipe(
    map(([id, ids, multi, nodes]) => {
      if (multi) {
        if (!ids || ids.length === 0) {
          return null;
        }
        const nodeIndex = findIndex(
          nodes,
          n => ids.indexOf(String(n.id)) !== -1,
        );
        return nodeIndex === -1 ? null : nodes[nodeIndex].id;
      } else {
        return id;
      }
    }),
    filter(id => !!id),
    replayLast(),
  );

  private optionHighlighted = new Subject<string>();

  private highlightedOptionId = merge(
    this.filter.pipe(map(filter => ({ type: 'filter', value: filter }))),
    this.nodesRecent.pipe(map(nodes => ({ type: 'nodes', value: nodes }))),
    this._filterable.pipe(
      map(filterable => ({ type: 'filterable', value: filterable })),
    ),
    this.firstSelectedId.pipe(
      map(option => ({ type: 'selected', value: option })),
    ),
    this.keyPress.pipe(map(key => ({ type: 'key', value: key }))),
    this.optionHighlighted.pipe(map(id => ({ type: 'change', value: id }))),
  ).pipe(
    scan<
      { type: string; value: any },
      {
        index: number;
        changed: boolean;
        filter: any;
        filterDirty: boolean;
        filterInit: boolean;
        filterable: any;
        selected: any;
        nodes: any[];
      }
    >(
      (state, event) => {
        const prevIndex = state.index;
        switch (event.type) {
          case 'filter':
            if (state.filter !== event.value) {
              if (!state.filter && event.value) {
                state.filterInit = true;
              }
              state.filter = event.value;
              state.filterDirty = true;
            }
            break;
          case 'nodes':
            state.nodes = event.value;
            state.changed = true;
            break;
          case 'filterable':
            state.filterable = event.value;
            break;
          case 'selected':
            state.selected = event.value;
            break;
          case 'key':
            switch (event.value) {
              case 'ArrowDown':
                state.index = this.getNextIndex(state.index, state.nodes);
                break;
              case 'ArrowUp':
                state.index = this.getPreviousIndex(state.index, state.nodes);
                break;
            }
            break;
          case 'change':
            state.index = state.nodes.findIndex(
              node => node.id === event.value,
            );
            break;
        }
        if (
          state.filterable === false &&
          state.filterDirty &&
          state.nodes.length
        ) {
          // prefix match
          const matchIndex = state.nodes.findIndex(
            node =>
              state.filter &&
              node.title.toLowerCase().startsWith(state.filter.toLowerCase()),
          );
          if (matchIndex >= 0) {
            state.index = matchIndex;
          }
          state.filterDirty = false;
        }
        if (state.nodes.length && state.index === -1 && state.selected) {
          // initialize highlight to selected value
          state.index = state.nodes.findIndex(
            node => node.id === state.selected,
          );
        }
        if (
          state.filterable &&
          state.filterDirty &&
          state.nodes.length &&
          state.filterInit
        ) {
          // tend toward putting the highlight on the first item
          // when they start searching
          state.index = 0;
          state.filterInit = false;
        }
        if (
          state.filterable &&
          state.filterDirty &&
          state.nodes.length &&
          state.index === -1
        ) {
          // fall back to highlighting the first item
          state.index = 0;
        }
        if (state.index !== prevIndex) {
          state.changed = true;
        }
        return state;
      },
      {
        index: -1,
        changed: false,
        filter: undefined,
        filterDirty: false,
        filterInit: false,
        filterable: undefined,
        selected: undefined,
        nodes: [],
      },
    ),
    filter(state => state.changed),
    map(state =>
      state.nodes.length && state.index >= 0
        ? state.nodes[
            this.getNextIndex(
              Math.min(state.nodes.length - 1, state.index) - 1,
              state.nodes,
            )
          ].id
        : null,
    ),
    replayLast(),
    startWith(-1),
  );

  nodes: Observable<Node[]> = combineLatest([
    this.nodesLimited,
    this.highlightedOptionId,
    this.combinedSelectedIds,
  ]).pipe(
    map(([nodes, highlightedOptionId, selectedIds]) => {
      if (!highlightedOptionId && selectedIds.length === 0) {
        return nodes;
      }
      const result: Node[] = [];
      nodes.forEach((node, nodeIndex) => {
        const selected = selectedIds.indexOf(String(node.id)) !== -1;
        if (node.id === highlightedOptionId || selected) {
          result.push({
            ...node,
            selected,
            highlighted: node.id === highlightedOptionId,
          });
        } else {
          result.push(node);
        }
      });
      return result;
    }),
  );

  allNodes: Observable<Node[]> = combineLatest([
    this.nodesRecent,
    this.highlightedOptionId,
    this.combinedSelectedIds,
  ]).pipe(
    map(([nodes, highlightedOptionId, selectedIds]) => {
      if (!highlightedOptionId && selectedIds.length === 0) {
        return nodes;
      }
      const result: Node[] = [];
      nodes.forEach((node, nodeIndex) => {
        const selected = selectedIds.indexOf(String(node.id)) !== -1;
        if (node.id === highlightedOptionId || selected) {
          result.push({
            ...node,
            selected,
            highlighted: node.id === highlightedOptionId,
          });
        } else {
          result.push(node);
        }
      });
      return result;
    }),
  );

  readonly selectedNodes: Observable<Node[]> = this.allNodes.pipe(
    map(nodes => nodes.filter(node => !!node.selected || this.all)),
  );

  notShownCount: Observable<number> = combineLatest([
    this.matchedNodes,
    this.nodesLimited,
  ]).pipe(
    map(([nodes, nodesLimited]) =>
      Math.max(0, nodes.count - nodesLimited.length),
    ),
  );

  @HostListener('keyup', ['$event'])
  @HostListener('keydown', ['$event'])
  keyUp($event: any) {
    const key = $event.key;
    if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter') {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if ($event.type === 'keydown') {
      this.internalKeyPress.next(key);
    }
  }

  private keyHandler = this.keyPress.pipe(
    withLatestFrom(
      this.defaultedFilterable,
      this.defaultedMultiSelect,
      this.combinedSelectedIds,
      this.highlightedOptionId,
    ),
    tap(([key, filterable, multiSelect, selectedIds, highlightedId]) => {
      if ((!multiSelect && key === 'Tab') || key === 'Enter') {
        if (multiSelect && selectedIds.indexOf(highlightedId) !== -1) {
          this.onNodeUnselected(highlightedId);
        } else {
          if (this.allowSelectingEmpty || highlightedId != null) {
            this.onNodeSelected(highlightedId);
          }
        }
        if (!filterable || !multiSelect) {
          this.filter.next('');
        }
      }
      if (!filterable && key.length === 1) {
        this.filter.next(this.filter.value + key);
      }
    }),
  );

  private externalScrollSubject = new Subject();

  public scrollSelectedIntoView() {
    this.externalScrollSubject.next(undefined);
  }

  private scrollToIndex(index: number) {
    const doScroll = (index: number) => {
      if (this.nodeRef) {
        const nodeRefs = this.nodeRef.toArray();
        if (nodeRefs.length > index) {
          const el = this.nodeRef.toArray()[index];
          if (el.nativeElement) {
            if (el.nativeElement.scrollIntoView) {
              el.nativeElement.scrollIntoView({ block: 'nearest' });
            } else {
              // for templates, all we have is the container (comment)
              // but we can find the node as nextElementSibling
              el.nativeElement.nextElementSibling.scrollIntoView({
                block: 'nearest',
              });
            }
          }
        }
      }
    };
    if (this.nodeRef) {
      doScroll(index);
    } else {
      setTimeout(() => {
        doScroll(index);
      }, 0);
    }
  }

  private scrollToOption(optionId: string, nodes: Node[]) {
    const index = nodes.findIndex(node => node.id === optionId);
    if (index >= 0) {
      this.scrollToIndex(index);
    }
  }

  private scrollHandler = this.highlightedOptionId.pipe(
    withLatestFrom(this.nodes),
    tap(([optionId, nodes]) => this.scrollToOption(optionId, nodes)),
  );

  private externalScrollHandler = this.externalScrollSubject.pipe(
    withLatestFrom(this.highlightedOptionId, this.nodes),
    tap(([_, optionId, nodes]) => this.scrollToOption(optionId, nodes)),
  );

  onNodePinned(id: string) {
    this.onPinnedEvent.emit([id, true]);
    this.optionIdPinned.next(id);
  }

  onNodeUnpinned(id: string) {
    this.onPinnedEvent.emit([id, false]);
    this.optionIdUnpinned.next(id);
  }

  getPinned(name: number) {
    if (this.pinned === undefined) {
      return false;
    }
    return this.pinned[name] === undefined ? false : this.pinned[name].pinned;
  }

  onNodeSelected(id: string) {
    if (this.recentIdentifier) {
      this.recentOptionsService.enqueue(id, this.recentIdentifier);
      this.recentOptionsSubject.next(undefined);
    }
    this.optionHighlighted.next(id);
    this.optionIdSelected.next(id);
  }

  onNodeUnselected(id: string) {
    this.optionHighlighted.next(id);
    this.optionIdUnselected.next(id);
  }

  onNodeUnselectedNoScroll(id: string, node: Node) {
    this.optionHighlighted.next(node.id.toString());
    this.optionIdUnselected.next(id);
  }

  private changeHandler = merge(
    this.optionIdSelected.pipe(map(id => ({ action: 'select', id }))),
    this.optionIdUnselected.pipe(map(id => ({ action: 'unselect', id }))),
  ).pipe(
    withLatestFrom(this.combinedSelectedIds, this.defaultedMultiSelect),
    tap(([event, selectedIds, multiSelect]) => {
      if (event.action === 'select') {
        if (multiSelect) {
          selectedIds.push(event.id);
          this.selectedOptionIdsChange.next([...selectedIds]);
        } else {
          this.selectedOptionIdChange.next(event.id);
        }
      } else {
        selectedIds.splice(selectedIds.indexOf(event.id), 1);
        this.selectedOptionIdsChange.next([...selectedIds]);
      }
    }),
  );

  subs = merge(
    this.keyHandler.pipe(
      map(() => {
        return true;
      }),
    ),
    this.changeHandler.pipe(
      map(() => {
        return true;
      }),
    ),
    this.scrollHandler.pipe(
      map(() => {
        return true;
      }),
    ),
    this.externalScrollHandler.pipe(
      map(() => {
        return true;
      }),
    ),
  );

  trackBy(index: number, node: Node): string {
    return node.header ? node.title : node.id.toString();
  }
  handleDrop(event: CdkDragDrop<Node[]>) {
    const shift = event.previousIndex - event.currentIndex;
    if (shift !== 0) {
      this.onDragEvent.emit([event.previousIndex, event.currentIndex]);
    }
  }

  setMinWidth(width: number) {
    if (width > this.minWidth) {
      this.minWidth = width;
    }
  }

  showMore() {
    this.usingLoader
      ? this.optionsLimitIncrement.next(OPTIONS_LIMIT)
      : (this.showAllNodes = true);
  }
}
